/**
 * 角色与权限（租户管理员）
 *
 * 权限树直接来自 @ilc/shared 的 PERMISSION_CATALOG（按 module 分组），
 * 与后端鉴权用的是同一份清单——不会出现「前端能勾、后端不认」的项。
 *
 * 每个角色除了权限码，还有三个决定「能审多大、能放多险」的配置：
 *   数据权限范围、可审核金额上限、可放行的最高风险等级。
 * 这三项正是文档 4.1 对财务初审岗明确要求的可配置项，因此放在同一表单里。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { LockOutlined, PlusOutlined, SafetyOutlined, WarningOutlined } from '@ant-design/icons';
import {
  ALL_PERMISSION_CODES,
  DATA_SCOPE_LABELS,
  DEFAULT_ROLES,
  DataScope,
  DefaultRoleCode,
  PERMISSION_CATALOG,
  PermissionCode,
  RISK_LEVEL_LABELS,
  RiskLevel,
  type Dept,
  type Role,
} from '@ilc/shared';
import { orgApi } from '@/api/endpoints';
import { mockDeptList, mockRoleList } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { RiskTag } from '@/components/RiskTag';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

/** 高危权限：勾选时给出额外提示（都不是「不能给」，而是「给了要清楚后果」） */
const DANGEROUS_PERMISSIONS: Partial<Record<PermissionCode, string>> = {
  [PermissionCode.TAX_INPUT_LOCK]:
    '「进项锁定」是不可逆写操作，会直接影响纳税申报，建议只授予实际负责申报的财务人员。',
  [PermissionCode.INVOICE_SPECIAL_RELEASE]:
    '「特例放行」允许高风险发票（红冲/作废）绕过默认阻断入账，是审计重点核查项，建议只授予财务负责人。',
  [PermissionCode.ROLE_WRITE]: '「管理角色」可以给自己或他人扩权，等同于租户内的最高权限。',
  [PermissionCode.CHECK_MANUAL_OVERRIDE]:
    '「人工接管校验」会在外部校验不可用时以人工说明替代系统校验，需由具备专业判断能力的人持有。',
};

function PermissionTree({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}): React.ReactElement {
  const grouped = useMemo(() => {
    const map = new Map<string, PermissionDefType[]>();
    for (const permission of PERMISSION_CATALOG) {
      const list = map.get(permission.module);
      if (list) list.push(permission);
      else map.set(permission.module, [permission]);
    }
    return Array.from(map.entries());
  }, []);

  const toggleModule = (module: string, checked: boolean): void => {
    const codes = PERMISSION_CATALOG.filter((p) => p.module === module).map((p) => p.code as string);
    onChange(checked ? Array.from(new Set([...value, ...codes])) : value.filter((c) => !codes.includes(c)));
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={10}>
      {grouped.map(([module, items]) => {
        const codes = items.map((p) => p.code as string);
        const selected = codes.filter((c) => value.includes(c));
        const allChecked = selected.length === codes.length;
        const indeterminate = selected.length > 0 && !allChecked;
        return (
          <Card key={module} size="small" styles={{ body: { padding: 10 } }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Space>
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  disabled={disabled}
                  onChange={(e) => toggleModule(module, e.target.checked)}
                >
                  <Typography.Text strong>{module}</Typography.Text>
                </Checkbox>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {selected.length}/{codes.length}
                </Typography.Text>
              </Space>
              <Checkbox.Group
                value={selected}
                disabled={disabled}
                onChange={(next) => {
                  const others = value.filter((c) => !codes.includes(c));
                  onChange([...others, ...(next as string[])]);
                }}
              >
                <Row gutter={[8, 2]}>
                  {items.map((permission) => {
                    const warning = DANGEROUS_PERMISSIONS[permission.code as PermissionCode];
                    return (
                      <Col xs={24} md={12} key={permission.code}>
                        <Checkbox value={permission.code}>
                          <Tooltip title={permission.description}>
                            <span>
                              {permission.name}
                              {warning ? (
                                <WarningOutlined style={{ color: '#d46b08', marginInlineStart: 4 }} />
                              ) : null}
                            </span>
                          </Tooltip>
                        </Checkbox>
                      </Col>
                    );
                  })}
                </Row>
              </Checkbox.Group>
            </Space>
          </Card>
        );
      })}
    </Space>
  );
}

/** 仅用于上面分组的小工具类型（避免把 readonly 数组当可变数组用） */
type PermissionDefType = (typeof PERMISSION_CATALOG)[number];

export default function Roles(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.ROLE_WRITE);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [dataScope, setDataScope] = useState<DataScope>(DataScope.DEPT);

  const roles = useResource<Role[]>(() => orgApi.roles(), { fallback: () => mockRoleList() });
  const depts = useResource<Dept[]>(() => orgApi.depts(), { fallback: () => mockDeptList() });

  const openCreate = (): void => {
    setCreating(true);
    setPermissions([]);
    setDataScope(DataScope.DEPT);
  };

  const openEdit = (role: Role): void => {
    setEditing(role);
    setPermissions(role.permissions ?? []);
    setDataScope(role.dataScope);
  };

  /**
   * 表单初值。
   *
   * 【为什么用 initialValues 而不是 setFieldsValue】
   * 与 `LegalWorkbench.tsx` 同一套做法（那里把这个坑记得更早、更细）。
   * 弹窗带 `destroyOnHidden`，关闭时会卸载内部的 `<Form>`；此刻 form 实例处于
   * "未连接"状态，在 openEdit 里同步调 `setFieldsValue` 会被**直接丢弃**，
   * 等 Form 重新挂载时又按 initialValues（空）初始化——结果就是
   * **编辑弹窗一片空白，而标题却是对的**（title 来自 state），
   * 看起来像"数据没取到"，实际是填值的时机不对。
   *
   * 把值交给 Form 自己按 key 初始化最稳，不必和挂载时序赛跑。
   * 回归测试：`packages/web/e2e/verify-role-edit-form.mjs`。
   */
  const formInitialValues = useMemo(
    () =>
      editing
        ? {
            code: editing.code,
            name: editing.name,
            description: editing.description ?? '',
            dataScope: editing.dataScope,
            customDeptIds: editing.customDeptIds ?? [],
            amountLimit:
              editing.amountLimitCents === null ? undefined : editing.amountLimitCents / 100,
            maxRiskLevel: editing.maxRiskLevel ?? undefined,
          }
        : { dataScope: DataScope.DEPT },
    [editing],
  );

  const applyPreset = (presetCode: DefaultRoleCode): void => {
    const preset = DEFAULT_ROLES.find((r) => r.code === presetCode);
    if (!preset) return;
    form.setFieldsValue({
      name: preset.name,
      description: preset.description,
      dataScope: preset.dataScope,
      amountLimit: preset.amountLimitCents === null ? undefined : preset.amountLimitCents / 100,
      maxRiskLevel: preset.maxRiskLevel ?? undefined,
    });
    setPermissions([...preset.permissions]);
    setDataScope(preset.dataScope);
    feedback.info(`已套用「${preset.name}」默认权限模板，可在此基础上调整`);
  };

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    if (permissions.length === 0) {
      feedback.warning('请至少勾选一项功能权限，否则该角色登录后看不到任何菜单');
      return;
    }
    setSaving(true);
    const body = {
      code: String(values.code ?? ''),
      name: String(values.name ?? ''),
      description: values.description ? String(values.description) : undefined,
      dataScope,
      ...(dataScope === DataScope.CUSTOM
        ? { customDeptIds: (values.customDeptIds ?? []) as string[] }
        : {}),
      amountLimit: values.amountLimit === undefined || values.amountLimit === null ? null : Number(values.amountLimit),
      maxRiskLevel: (values.maxRiskLevel ?? null) as RiskLevel | null,
      permissions,
    };
    const result = await performAction({
      action: () => (editing ? orgApi.updateRole({ id: editing.id, ...body }) : orgApi.createRole(body)),
      successMessage: editing ? '角色已更新，权限立即生效' : '角色已创建',
      offline: () => undefined,
      offlineMessage: '后端角色接口尚未就绪，本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setCreating(false);
      setEditing(null);
      form.resetFields();
      roles.reload();
    }
  };

  const removeRole = async (reason: string): Promise<void> => {
    if (!deleteTarget) return;
    const result = await performAction({
      action: () => Promise.reject(new Error('角色删除接口尚未开放')),
      successMessage: '角色已删除',
      offline: () => undefined,
      offlineMessage: `后端未提供角色删除接口；本次仅在本地演示。删除理由：${reason}`,
    });
    setDeleteTarget(null);
    if (result.ok) roles.reload();
  };

  const permissionCount = (role: Role): number => role.permissions?.length ?? 0;

  return (
    <div className="ilc-page">
      <PageHeader
        title="角色与权限"
        description="维护租户内角色：功能权限、数据权限范围、可审核金额上限、可放行风险等级。权限清单与服务端共用同一份目录。"
        extra={
          <Space>
            <Button icon={<SafetyOutlined />} onClick={() => roles.reload()}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={!canWrite} onClick={openCreate}>
              新增角色
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="权限调整的影响范围"
        description="角色权限变更后，持有该角色的用户下次请求立即生效（无需重新登录）。前端菜单与按钮的显隐只是体验优化，真正的权限判定全部在服务端——因此不要用「把菜单藏起来」当作权限控制手段。"
      />

      <DataTable<Role>
        storageKey="admin-roles"
        exportFileName="角色列表"
        exportColumns={[
          { title: '角色名称', dataIndex: 'name' },
          { title: '角色编码', dataIndex: 'code' },
          { title: '数据权限', value: (r) => DATA_SCOPE_LABELS[r.dataScope] },
          { title: '权限数', value: (r) => r.permissions?.length ?? 0 },
          { title: '用户数', value: (r) => r.userCount ?? 0 },
        ]}
        columns={[
          {
            title: '角色名称',
            dataIndex: 'name',
            width: 160,
            fixed: 'left',
            render: (v: string, record) => (
              <Space size={4}>
                <span>{v}</span>
                {record.isSystem ? (
                  <Tooltip title="平台预置角色模板，不可删除">
                    <LockOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: '角色编码',
            dataIndex: 'code',
            width: 150,
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          { title: '描述', dataIndex: 'description', width: 220, ellipsis: true, render: (v: string | null) => v ?? '-' },
          {
            title: '数据权限',
            dataIndex: 'dataScope',
            width: 130,
            render: (v: DataScope, record) => (
              <Tooltip
                title={
                  v === DataScope.CUSTOM
                    ? `自定义部门：${(record.customDeptIds ?? []).join('、') || '未选择'}`
                    : DATA_SCOPE_LABELS[v]
                }
              >
                <Tag color="blue">{DATA_SCOPE_LABELS[v]}</Tag>
              </Tooltip>
            ),
          },
          {
            title: '可审核金额上限',
            dataIndex: 'amountLimitCents',
            width: 150,
            align: 'right',
            render: (v: number | null) =>
              v === null ? (
                <Tag color="orange">不限</Tag>
              ) : (
                <span className="ilc-mono">¥{(v / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
              ),
          },
          {
            title: '可放行风险等级',
            dataIndex: 'maxRiskLevel',
            width: 150,
            render: (v: RiskLevel | null) => (v ? <RiskTag level={v} withHint={false} /> : <Tag>不限</Tag>),
          },
          {
            title: '功能权限',
            key: 'permissions',
            width: 120,
            render: (_v, record) => (
              <Tooltip
                title={(record.permissions ?? [])
                  .map((code) => PERMISSION_CATALOG.find((p) => p.code === code)?.name ?? code)
                  .join('、')}
              >
                <Tag color={permissionCount(record) === 0 ? 'orange' : 'blue'}>
                  {permissionCount(record)} 项
                </Tag>
              </Tooltip>
            ),
          },
          {
            title: '用户数',
            dataIndex: 'userCount',
            width: 90,
            render: (v: number | undefined) => v ?? 0,
          },
          {
            title: '操作',
            key: 'action',
            width: 170,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4}>
                <Button size="small" type="link" disabled={!canWrite} onClick={() => openEdit(record)}>
                  {canWrite ? '编辑权限' : '查看权限'}
                </Button>
                <Button
                  size="small"
                  type="link"
                  danger
                  disabled={!canWrite || record.isSystem}
                  onClick={() => setDeleteTarget(record)}
                >
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
        dataSource={roles.data ?? []}
        rowKey="id"
        loading={roles.loading}
        error={roles.error}
        isFallback={roles.isFallback}
        fallbackReason={roles.fallbackReason}
        onRetry={roles.reload}
        pagination={false}
        emptyText="暂无角色，点击右上角「新增角色」创建"
      />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card size="small" title="默认角色模板（平台预置）">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="预置模板体现的是最小必要原则"
              description="例如财务记账岗默认拿不到审核权、技术实施组默认看不到发票金额。需要时可由租户管理员显式授予——这比事后收紧更安全。"
            />
            <Table
              size="small"
              rowKey="code"
              pagination={false}
              dataSource={DEFAULT_ROLES}
              columns={[
                { title: '角色', dataIndex: 'name', width: 130 },
                {
                  title: '数据权限',
                  dataIndex: 'dataScope',
                  width: 110,
                  render: (v: DataScope) => DATA_SCOPE_LABELS[v],
                },
                {
                  title: '金额上限',
                  dataIndex: 'amountLimitCents',
                  width: 130,
                  align: 'right',
                  render: (v: number | null) =>
                    v === null ? '不限' : <span className="ilc-mono">¥{(v / 100).toLocaleString('zh-CN')}</span>,
                },
                {
                  title: '最高风险',
                  dataIndex: 'maxRiskLevel',
                  width: 110,
                  render: (v: RiskLevel | null) => (v ? RISK_LEVEL_LABELS[v] : '不限'),
                },
                {
                  title: '默认权限',
                  dataIndex: 'permissions',
                  render: (v: readonly string[]) => (
                    <Tooltip
                      title={v.map((code) => PERMISSION_CATALOG.find((p) => p.code === code)?.name ?? code).join('、')}
                    >
                      <Tag color="blue">{v.length} 项</Tag>
                    </Tooltip>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="权限模型速览">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="功能权限">
                {PERMISSION_CATALOG.length} 项，格式「资源:动作」，同时用于菜单、按钮与接口级鉴权。
              </Descriptions.Item>
              <Descriptions.Item label="数据权限">
                {Object.values(DATA_SCOPE_LABELS).join(' / ')}。服务端自动改写查询范围。
              </Descriptions.Item>
              <Descriptions.Item label="金额上限">
                财务初审岗的可配置项：超出上限的发票无法通过，需更高权限人员处理。
              </Descriptions.Item>
              <Descriptions.Item label="风险等级上限">
                可放行的最高风险等级。默认模板中财务初审岗为「{RISK_LEVEL_LABELS[RiskLevel.MEDIUM]}」，
                即高风险票必须走特例放行。
              </Descriptions.Item>
              <Descriptions.Item label="高危权限">共 {Object.keys(DANGEROUS_PERMISSIONS).length} 项，勾选时有橙色警示</Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '8px 0' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              前端权限判断只影响「看不看得见」，真实拦截在服务端。若发现「按钮能点但接口返回 403」，
              说明前端权限码与服务端不一致，请技术实施组核对权限目录。
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Modal
        open={creating || editing !== null}
        title={editing ? `编辑角色 ${editing.name}` : '新增角色'}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => void save()}
        confirmLoading={saving}
        width={860}
        destroyOnHidden
        okText="保存"
      >
        {/*
          key 取被编辑角色的 id：从"编辑 A"切到"编辑 B"、或切到"新增"时，
          Form 会整体重建并按 initialValues 初始化，不会有上一条的残留值。
          （这正是 e2e 里"换角色后填的是不是这一条"那条断言在防的）
        */}
        <Form
          key={editing?.id ?? 'create'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={formInitialValues}
        >
          {!editing ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="可以从默认模板开始"
              description={
                <Space wrap size={[4, 4]}>
                  {DEFAULT_ROLES.map((preset) => (
                    <Button key={preset.code} size="small" onClick={() => applyPreset(preset.code)}>
                      套用「{preset.name}」
                    </Button>
                  ))}
                </Space>
              }
            />
          ) : null}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="角色编码"
                rules={[
                  { required: true, message: '请输入角色编码' },
                  { pattern: /^[A-Z][A-Z0-9_]{2,31}$/, message: '大写字母开头，仅含大写字母、数字与下划线' },
                ]}
                extra="编码用于接口鉴权与日志，建议与语义一致，如 FINANCE_REVIEWER"
              >
                <Input className="ilc-mono" disabled={editing?.isSystem} placeholder="FINANCE_REVIEWER" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
                <Input placeholder="如 财务初审岗" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dataScope" label="数据权限范围" rules={[{ required: true }]}>
                <Select
                  onChange={(value: DataScope) => setDataScope(value)}
                  options={Object.entries(DATA_SCOPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            {dataScope === DataScope.CUSTOM ? (
              <Col span={24}>
                <Form.Item name="customDeptIds" label="自定义部门范围" rules={[{ required: true, message: '请选择部门' }]}>
                  <Select
                    mode="multiple"
                    placeholder="选择可见的部门"
                    options={(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                  />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={8}>
              <Form.Item
                name="amountLimit"
                label="可审核金额上限（元）"
                extra="留空表示不限；超限发票初审无法通过"
              >
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  step={10000}
                  placeholder="如 500000"
                  formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => Number((value ?? '').replace(/,/g, ''))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxRiskLevel" label="可放行最高风险等级" extra="高风险发票需特例放行权限">
                <Select
                  allowClear
                  placeholder="不限"
                  options={Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="description" label="角色描述">
                <Input placeholder="这个角色负责什么" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            功能权限（已选 {permissions.length} / {ALL_PERMISSION_CODES.length} 项）
          </Divider>
          <Space style={{ marginBottom: 8 }} wrap>
            <Button size="small" disabled={!canWrite} onClick={() => setPermissions([...ALL_PERMISSION_CODES])}>
              全选（等于管理员）
            </Button>
            <Button size="small" disabled={!canWrite} onClick={() => setPermissions([])}>
              清空
            </Button>
            {Object.keys(DANGEROUS_PERMISSIONS).length > 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <WarningOutlined style={{ color: '#d46b08' }} /> 标记项为高危权限
              </Typography.Text>
            ) : null}
          </Space>
          <PermissionTree value={permissions} onChange={setPermissions} disabled={!canWrite} />

          {Object.entries(DANGEROUS_PERMISSIONS)
            .filter(([code]) => permissions.includes(code))
            .slice(0, 2)
            .map(([code, warning]) => (
              <Alert key={code} type="warning" showIcon style={{ marginTop: 8 }} message={warning} />
            ))}
        </Form>
      </Modal>

      <ConfirmDanger
        open={deleteTarget !== null}
        title={`删除角色「${deleteTarget?.name ?? ''}」`}
        level="high"
        confirmText="确认删除"
        acknowledgeText="我已知晓：若仍有用户持有该角色，删除后他们会失去对应权限"
        description={
          <Space direction="vertical" size={4}>
            <span>
              该角色当前关联 <strong>{deleteTarget?.userCount ?? 0}</strong> 个用户。
            </span>
            <span>删除后这些用户将立即失去该角色带来的权限（含数据范围与金额/风险上限），可能导致其无法继续处理待办。</span>
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>
              建议先到「用户管理」把这些用户改派到其它角色，再删除本角色。
            </span>
          </Space>
        }
        reasonLabel="删除原因（必填，不少于 8 字）"
        reasonPresets={['岗位取消，权限已合并到新角色', '角色配置有误，改为新建角色替代']}
        onConfirm={(reason) => removeRole(reason)}
        onCancel={() => setDeleteTarget(null)}
      />

      {(roles.data ?? []).length === 0 && !roles.loading ? (
        <Empty
          style={{ marginTop: 16 }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="角色接口未就绪或该租户尚未初始化角色，可先点击「新增角色」体验权限树"
        />
      ) : null}
    </div>
  );
}
